import { DNAButton, DNAChip, DNAChipStatusEnum, DNAText, GridList, luxColors } from '@alucio/lux-ui'
import React, { useMemo, useState } from 'react'
import { StyleSheet } from 'react-native'
import format from 'date-fns/format';

import TrainingEventContextMenu from 'src/screens/Tenants/Tabs/Events/TrainingEventContextMenu'
import { TenantORM } from 'src/state/redux/selector/tenant'
import { TrainingEventORM } from 'src/types/orms'

const styles = StyleSheet.create({
  column: {
    paddingVertical: 12,
    paddingHorizontal: 28,
  },
  chipStyle: {
    borderRadius: 20,
    paddingHorizontal: 8,
  },
  hoveredRow: {
    backgroundColor: luxColors.backgroundColor.secondary,
  },
  itemsWrapper: {
    flex: 1,
    flexDirection: 'column',
  },
  itemTextColor: {
    color: luxColors.contentText.tertiary,
  },
});

enum TrainingEventStatus {
  IN_PROGRESS = 'IN PROGRESS',
  UPCOMING = 'UPCOMING',
  COMPLETED = 'COMPLETED',
}

interface Props {
  trainingEvent: TrainingEventORM
  tenantORM: TenantORM,
}

const DNATrainingEventDefaultRow = (props: Props) => {
  const { trainingEvent } = props

  const [isHovered, setIsHovered] = useState<boolean>(false);

  function onMouseEnter(): void {
    setIsHovered(true);
  }

  function onMouseLeave(): void {
    setIsHovered(false);
  }

  // considering typing the return type
  const getTrainingEventStatus = () => {
    // note we are assuming that endDateTime is always after startDateTime.
    // consider checking here if endDateTime is before startDateTime then throw error for better code quality
    const { startDateTime, endDateTime } = trainingEvent.model
    const now = Date.now()
    const startDateTimeStamp = new Date(startDateTime).getTime()
    const endDateTimeStamp = new Date(endDateTime).getTime()
    if (startDateTimeStamp > now) {
      return {
        text: TrainingEventStatus.UPCOMING,
        status: DNAChipStatusEnum.basic,
      }
    } else if (startDateTimeStamp < now && endDateTimeStamp > now) {
      return {
        text: TrainingEventStatus.IN_PROGRESS,
        status: DNAChipStatusEnum.primary,
      }
    } else {
      return {
        text: TrainingEventStatus.COMPLETED,
        status: DNAChipStatusEnum.success,
      }
    }
  }
  const trainingEventStatus = useMemo(
    () => getTrainingEventStatus(),
    [trainingEvent.model.startDateTime, trainingEvent.model.endDateTime])

  return (
    <GridList.Row
      style={isHovered && styles.hoveredRow}
      onMouseLeave={onMouseLeave}
      onMouseEnter={onMouseEnter}
    >
      {/* Title */}
      <GridList.Col style={styles.column} span={6}>
        <DNAText numberOfLines={1} style={styles.itemTextColor}>
          {trainingEvent.model.title}
        </DNAText>
      </GridList.Col>
      {/* Start time */}
      <GridList.Col style={styles.column} span={4}>
        <DNAText style={styles.itemTextColor}>
          {format(new Date(trainingEvent.model.startDateTime), 'LLL d, y, p')} PST
        </DNAText>
      </GridList.Col>
      {/* End time */}
      <GridList.Col style={styles.column} span={4}>
        <DNAText numberOfLines={1} style={styles.itemTextColor}>
          {format(new Date(trainingEvent.model.endDateTime), 'LLL d, y, p')} PST
        </DNAText>
      </GridList.Col>
      {/* status */}
      <GridList.Col style={styles.column} span={3}>
        <DNAText numberOfLines={1} style={styles.itemTextColor}>
          <DNAChip
            status={trainingEventStatus.status}
            appearance="subtle"
            style={styles.chipStyle}
          >
            {trainingEventStatus.text}
          </DNAChip>
        </DNAText>
      </GridList.Col>
      <GridList.Col style={[styles.column, { justifyContent: 'center' }]} span={1}>
        <TrainingEventContextMenu trainingEvent={trainingEvent}>
          <DNAButton
            status="gray"
            appearance="ghost"
            iconLeft="dots-vertical"
            size="md"
          />
        </TrainingEventContextMenu>
      </GridList.Col>
    </GridList.Row>
  );
}

export default DNATrainingEventDefaultRow
